import type { Fetcher, SWRConfiguration } from 'swr';
import useSWR, { mutate } from 'swr';

import { contractsList, contractsRetrieve } from '~/data/generated/endpoints/contracts/contracts';
import type { ContractsListParams } from '~/data/generated/model';
import { PROPERTY_CONTRACTS_KEY } from '~/data/hooks/usePropertyContracts';
import { useRefetchedOnOpen } from '~/data/hooks/useRefetchedOnOpen';
import { parseApiResponse } from '~/data/parseApiResponse';
import { type Contract, contractSchema } from '~/data/schemas/contract';

const CONTRACT_KEY = 'contract' as const;

/**
 * The app-wide filter bar's scope, as the contracts list endpoint takes it. Portfolio
 * and region join through the properties a contract covers, so a contract spanning
 * several matches if any one of them is in scope. An omitted key means "All".
 */
export type ContractsScope = Pick<ContractsListParams, 'portfolio' | 'region' | 'service_provider'>;

// The scope sits at the key's second slot as an object, which is the shape
// `getMutatorForType` recognises as "the list, under some params" — that is what makes
// a saved contract revalidate the list rather than only its own record.
const contractsListFetcher: Fetcher<
  Contract[],
  readonly [typeof CONTRACT_KEY, scope: ContractsScope]
> = async ([, scope]) => {
  // Unpaginated (no `limit`), so the endpoint returns a bare Contract[] rather
  // than the paginated envelope the generated type describes (AMP-504).
  const { data } = await contractsList(scope);
  const results = Array.isArray(data) ? data : data.results;
  return parseApiResponse(contractSchema.array(), results, 'Contract');
};

const contractFetcher: Fetcher<
  Contract,
  readonly [typeof CONTRACT_KEY, contractId: string]
> = async ([, contractId]) => {
  const { data } = await contractsRetrieve(contractId);
  return parseApiResponse(contractSchema, data, 'Contract');
};

const contractsByPropertyFetcher: Fetcher<
  Contract[],
  readonly [typeof CONTRACT_KEY, 'property', propertyId: string]
> = async ([, , propertyId]) => {
  // Same unpaginated shape as the unfiltered list (AMP-504).
  const { data } = await contractsList({ property: propertyId });
  const results = Array.isArray(data) ? data : data.results;
  return parseApiResponse(contractSchema.array(), results, 'Contract');
};

export function useContracts(
  scope: ContractsScope = {},
  options?: Pick<SWRConfiguration, 'onError'>,
) {
  // The scope is part of the cache key, so narrowing the filter bar refetches rather
  // than serving the previous scope's rows.
  const { data, error, isLoading } = useSWR(
    [CONTRACT_KEY, scope] as const,
    contractsListFetcher,
    options,
  );

  return {
    contracts: data ?? [],
    error,
    isLoading,
  };
}

/**
 * Contracts covering one property, with their nested `propertyContracts[].cars`.
 * Distinct from `usePropertyContracts`, whose rows carry no contract name or term
 * dates, and from `useContracts`, which pulls every contract the user can see.
 */
export function useContractsForProperty(
  propertyId: string | undefined,
  options?: Pick<SWRConfiguration, 'onError'>,
) {
  const { data, error, isLoading } = useSWR(
    propertyId ? ([CONTRACT_KEY, 'property', propertyId] as const) : null,
    contractsByPropertyFetcher,
    options,
  );

  return {
    contracts: data ?? [],
    error,
    isLoading,
  };
}

/**
 * Refresh every read of one property's contracts after a write.
 *
 * They live under two keys: `['contract', 'property', id]` for
 * {@link useContractsForProperty}, which the property page's contracts tab and the
 * invoice detail page read, and `['property-contracts', id]` for `usePropertyContracts`,
 * which the related-counts hook reads. Both are read independently, so a write has to
 * reach both or the list the user is looking at keeps the pre-save rows.
 */
export async function revalidatePropertyContracts(propertyId: string): Promise<void> {
  await Promise.all([
    mutate([CONTRACT_KEY, 'property', propertyId]),
    mutate([PROPERTY_CONTRACTS_KEY, propertyId]),
  ]);
}

export default function useContract(
  contractId: string | null,
  options?: Pick<SWRConfiguration, 'onError'>,
) {
  const { data, error, isLoading } = useSWR(
    contractId ? ([CONTRACT_KEY, contractId] as const) : null,
    contractFetcher,
    options,
  );

  return {
    contract: data,
    error,
    isLoading,
  };
}

export function useContractPreview(contractId: string | null) {
  // Cached document links may have expired, so show the contract only once refetched.
  const { data, error, loading } = useRefetchedOnOpen(
    contractId ? ([CONTRACT_KEY, contractId] as const) : null,
    contractFetcher,
  );

  return { contract: data, error, loading };
}
